import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import SourceLink from '../components/SourceLink';
import { SOURCES } from '../content/site';

const Problem: React.FC = () => (
  <div>
    <PageHero
      title="The forecast is the first line of reliability."
      lead="Balancing authorities commit generation and procure reserves against a day-ahead load forecast. When the forecast misses, reserves are short and load is shed. When telemetry is corrupted or misread, the forecast and the operator's picture are both wrong. The public record documents both failure modes."
    />

    <section className="bg-white section-padding">
      <div className="container-width">
        <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-8">What the public record says</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card
            title="Winter Storm Elliott (December 2022)"
            body="Most core balancing authorities significantly underestimated the peak loads of December 23 and 24. Day-ahead load was under-forecast by as much as 11.6% at one balancing authority, with peak-load forecast errors around 10% for most of them on December 23, contributing to emergency operations and firm load shed. The inquiry recommended that balancing authorities improve their short-term load forecasts for extreme cold."
            source={SOURCES.elliott}
          />
          <Card
            title="Arctic storms, January 2024 and January 2025"
            body="The FERC and NERC reviews record the largest day-ahead under-forecast by a balancing authority at 11.8% in Uri, 12.5% in Elliott and 14% in Gerri and Heather, with errors peaking early in the storms as the weather worsened. Forecasts improved in January 2025, but the first cold morning remains the hardest to forecast, and its readings are the ones a statistical filter is most likely to mistake for bad data."
            source={SOURCES.arctic2025}
          />
          <Card
            title="NERC 2025 Long-Term Reliability Assessment"
            body="Summer peak demand is projected to grow by 224 GW over ten years, a large upward revision in a single year, with data centers the largest driver. NERC reports that the flexible operation of large loads challenges accurate load forecasting, and that ERCOT has observed increasing load-forecast errors on the extreme days when accuracy matters most."
            source={SOURCES.ltra2025}
          />
          <Card
            title="DOE grid reliability report (July 2025)"
            body="Absent corrective action, annual loss-of-load hours could increase a hundred-fold by 2030, as peak demand grows from 774 GW to 889 GW and 104 GW of firm capacity retires without one-to-one replacement."
            source={SOURCES.doeRA2025}
          />
          <Card
            title="NERC on AI in real-time operations (2024)"
            body="Data validation, cleaning, anomaly detection and provenance are named as preconditions for trustworthy operator-facing AI."
            source={SOURCES.nercAI}
          />
          <Card
            title="DOE, AI for Energy (2024)"
            body="AI forecasting for grid operators and anomalous-event detection are identified as near-term priority use cases for a modern grid."
            source={SOURCES.doeAI}
          />
        </div>
      </div>
    </section>

    <section className="bg-gray-50 section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">The chain, in four steps</h2>
            <ol className="space-y-4 text-lg text-gray-700 list-decimal list-inside">
              <li>Balancing authorities commit generation and procure reserves against a day-ahead load forecast.</li>
              <li>When load is under-forecast, reserves are insufficient, emergency orders are issued, and firm load is shed.</li>
              <li>When telemetry is corrupted or misclassified, the forecast and the operator's situational awareness are both wrong.</li>
              <li>Under rapid, step-change load growth, both failure modes become more frequent.</li>
            </ol>
            <p className="text-gray-600 mt-6">
              Each percentage point of forecast error at a large balancing authority is hundreds of megawatts of
              reserve either missing during an emergency or bought unnecessarily on ordinary days, at a cost that
              ratepayers ultimately bear. Power interruptions already cost U.S. consumers on the order of $80 billion a
              year. <SourceLink source={SOURCES.lbnl} />
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mb-4">Where this initiative works</h3>
            <p className="text-gray-600 mb-6">
              On two links of the chain: the forecast, and the data the forecast depends on.
            </p>
            <div className="space-y-4">
              <div className="border-l-4 border-primary-red pl-4">
                <h4 className="font-montserrat font-semibold text-dark-gray mb-1">Measure the forecast</h4>
                <p className="text-gray-600 text-sm">
                  The <Link to="/scorecard" className="text-primary-red hover:underline">BA Forecast Scorecard</Link> scores
                  every balancing authority's own day-ahead forecast from public data, continuously.
                </p>
              </div>
              <div className="border-l-4 border-light-blue pl-4">
                <h4 className="font-montserrat font-semibold text-dark-gray mb-1">Protect the data</h4>
                <p className="text-gray-600 text-sm">
                  The <Link to="/sentinel" className="text-light-blue hover:underline">Grid Data Sentinel</Link> detects and
                  corrects telemetry anomalies while keeping genuine extremes, with an audit trail.
                </p>
              </div>
              <div className="border-l-4 border-dark-gray pl-4">
                <h4 className="font-montserrat font-semibold text-dark-gray mb-1">Publish the method</h4>
                <p className="text-gray-600 text-sm">
                  <Link to="/notes" className="text-dark-gray hover:underline">Technical notes</Link> and code so that any
                  operator, utility or researcher can reproduce and reuse the results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

const Card: React.FC<{ title: string; body: string; source: typeof SOURCES[keyof typeof SOURCES] }> = ({
  title,
  body,
  source,
}) => (
  <div className="bg-gray-50 rounded-xl p-8 shadow hover:shadow-lg transition-shadow flex flex-col">
    <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-3">{title}</h3>
    <p className="text-gray-600 mb-4 flex-1">{body}</p>
    <SourceLink source={source} />
  </div>
);

export default Problem;
